import { IUserRepository } from '../repository/interfaces';

/**
 * Reviews & Testimonials Use Case
 * Handles review creation, moderation, and management
 * User Stories: 15, 16
 */
export class ReviewsUseCase  {
  constructor(private userRepo: IUserRepository) {
    // Removed BaseUseCase
  }

  /**
   * Create a review for a purchased item
   * User Story 15: As a buyer, I can leave a review only for completed purchases; appears after moderation rule
   */
  async createReview(
    reviewData: {
      userId: string;
      itemType: 'course' | 'resource' | 'connect';
      itemId: string;
      rating: number;
      title: string;
      comment: string;
      pros?: string[];
      cons?: string[];
      recommendTo?: string;
    }
  ): Promise<{
    review: any;
    requiresModeration: boolean;
    estimatedPublishDate?: Date;
  }> {
    try {
      // Validate rating
      if (reviewData.rating < 1 || reviewData.rating > 5) {
        throw new Error('Rating must be between 1 and 5');
      }

      // Verify user has purchased/completed the item
      const hasAccess = await this.verifyPurchaseOrCompletion(
        reviewData.userId,
        reviewData.itemType,
        reviewData.itemId
      );

      if (!hasAccess) {
        throw new Error('You can only review items you have purchased or completed');
      }

      // Check if user already reviewed this item
      const existingReview = await this.getExistingReview(
        reviewData.userId,
        reviewData.itemType,
        reviewData.itemId
      );

      if (existingReview) {
        throw new Error('You have already reviewed this item');
      }

      // Create review
      const review = {
        id: `review_${Date.now()}`,
        userId: reviewData.userId,
        itemType: reviewData.itemType,
        itemId: reviewData.itemId,
        rating: reviewData.rating,
        title: reviewData.title,
        comment: reviewData.comment,
        pros: reviewData.pros,
        cons: reviewData.cons,
        recommendTo: reviewData.recommendTo,
        status: 'pendingModeration',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Apply moderation rules
      const moderationResult = await this.applyModerationRules(review);

      // Update review status
      review.status = moderationResult.status;
      review.moderationReason = moderationResult.reason;

      // Calculate estimated publish date
      let estimatedPublishDate: Date | undefined;
      if (review.status === 'pendingModeration') {
        estimatedPublishDate = new Date();
        estimatedPublishDate.setHours(estimatedPublishDate.getHours() + 24); // 24 hours for moderation
      }

      return {
        review,
        requiresModeration: review.status === 'pendingModeration',
        estimatedPublishDate
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Get reviews for an item
   */
  async getItemReviews(
    itemType: 'course' | 'resource' | 'connect',
    itemId: string,
    options: {
      status?: 'published' | 'pending' | 'rejected';
      sortBy?: 'newest' | 'oldest' | 'highestRating' | 'lowestRating' | 'mostHelpful';
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<{
    reviews: any[];
    stats: {
      total: number;
      averageRating: number;
      ratingDistribution: Record<number, number>;
      published: number;
      pending: number;
      rejected: number;
    };
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> {
    try {
      // Mock reviews data - in reality would query database
      const mockReviews = this.generateMockReviews(itemType, itemId, 15);

      // Apply status filter
      let filteredReviews = mockReviews;
      if (options.status) {
        filteredReviews = mockReviews.filter(r => r.status === options.status);
      }

      // Apply sorting
      switch (options.sortBy) {
        case 'newest':
          filteredReviews.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          break;
        case 'oldest':
          filteredReviews.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
          break;
        case 'highestRating':
          filteredReviews.sort((a, b) => b.rating - a.rating);
          break;
        case 'lowestRating':
          filteredReviews.sort((a, b) => a.rating - b.rating);
          break;
        case 'mostHelpful':
        default:
          filteredReviews.sort((a, b) => (b.helpfulVotes || 0) - (a.helpfulVotes || 0));
          break;
      }

      // Apply pagination
      const total = filteredReviews.length;
      const limit = options.limit || 10;
      const offset = options.offset || 0;
      const paginatedReviews = filteredReviews.slice(offset, offset + limit);

      // Get reviewer information
      const reviewsWithUsers = await Promise.all(
        paginatedReviews.map(async (review) => {
          const reviewer = await this.userRepo.getUserById(review.userId);
          return {
            ...review,
            reviewer: reviewer ? {
              id: reviewer.id,
              firstName: reviewer.firstName,
              lastName: reviewer.lastName,
              profileImageUrl: reviewer.profileImageUrl
            } : null
          };
        })
      );

      // Calculate statistics
      const publishedReviews = mockReviews.filter(r => r.status === 'published');
      const averageRating = publishedReviews.length > 0
        ? publishedReviews.reduce((sum, r) => sum + r.rating, 0) / publishedReviews.length
        : 0;

      const ratingDistribution: Record<number, number> = {};
      for (let i = 1; i <= 5; i++) {
        ratingDistribution[i] = publishedReviews.filter(r => r.rating === i).length;
      }

      const stats = {
        total: mockReviews.length,
        averageRating: Math.round(averageRating * 10) / 10,
        ratingDistribution,
        published: publishedReviews.length,
        pending: mockReviews.filter(r => r.status === 'pendingModeration').length,
        rejected: mockReviews.filter(r => r.status === 'rejected').length
      };

      return {
        reviews: reviewsWithUsers,
        stats,
        pagination: {
          total,
          limit,
          offset,
          hasMore: offset + limit < total
        }
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Moderate a review (Admin only)
   * User Story 16: As Admin, I moderate (approve/reject) reviews globally
   */
  async moderateReview(
    reviewId: string,
    adminId: string,
    action: 'approve' | 'reject',
    reason?: string
  ): Promise<{
    review: any;
    moderation: any;
    notificationsSent: boolean;
  }> {
    try {
      // Verify admin permissions
      const isAdmin = await this.userRepo.isUserAdmin(adminId);
      if (!isAdmin) {
        throw new Error('Unauthorized: Only administrators can moderate reviews');
      }

      // Get review (mock)
      const review = {
        id: reviewId,
        status: 'pendingModeration',
        userId: 'user123',
        itemType: 'course',
        itemId: 'course456'
      };

      // Update review status
      const newStatus = action === 'approve' ? 'published' : 'rejected';
      review.status = newStatus;
      review.moderatedAt = new Date();
      review.moderatedBy = adminId;
      review.moderationReason = reason;

      // Create moderation record
      const moderation = {
        id: `mod_${Date.now()}`,
        reviewId: reviewId,
        adminId: adminId,
        action,
        reason,
        moderatedAt: new Date()
      };

      // Send notification to reviewer
      const notificationsSent = await this.sendModerationNotification(
        review.userId,
        action,
        reason
      );

      return {
        review,
        moderation,
        notificationsSent
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Mark review as helpful
   */
  async markReviewHelpful(
    reviewId: string,
    userId: string,
    helpful: boolean
  ): Promise<{
    review: any;
    userVote: any;
  }> {
    try {
      // Check if user already voted
      const existingVote = await this.getUserVote(reviewId, userId);

      if (existingVote) {
        throw new Error('You have already voted on this review');
      }

      // Update review helpful count (mock)
      const review = {
        id: reviewId,
        helpfulVotes: 15,
        totalVotes: 20
      };

      if (helpful) {
        review.helpfulVotes += 1;
      }
      review.totalVotes += 1;

      // Record user vote
      const userVote = {
        id: `vote_${Date.now()}`,
        reviewId: reviewId,
        userId: userId,
        voteType: helpful ? 'helpful' : 'notHelpful',
        votedAt: new Date()
      };

      return {
        review,
        userVote
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Report a review for inappropriate content
   */
  async reportReview(
    reviewId: string,
    reporterId: string,
    reason: string,
    details?: string
  ): Promise<{
    report: any;
    review: any;
    autoModerated: boolean;
  }> {
    try {
      // Create report
      const report = {
        id: `report_${Date.now()}`,
        reviewId: reviewId,
        reporterId: reporterId,
        reason,
        details,
        status: 'pending',
        reportedAt: new Date()
      };

      // Get review for context
      const review = {
        id: reviewId,
        status: 'published',
        contentFlagged: false
      };

      // Check for auto-moderation rules
      const autoModerated = await this.applyAutoModeration(review, report);

      if (autoModerated) {
        review.status = 'flagged';
        review.contentFlagged = true;
      }

      return {
        report,
        review,
        autoModerated
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Get user's reviews
   */
  async getUserReviews(
    userId: string,
    options: {
      status?: 'published' | 'pending' | 'rejected';
      itemType?: 'course' | 'resource' | 'connect';
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<{
    reviews: any[];
    stats: {
      total: number;
      published: number;
      pending: number;
      rejected: number;
      averageRating: number;
    };
  }> {
    try {
      // Mock user reviews
      const mockReviews = this.generateMockUserReviews(userId, 8);

      // Apply filters
      let filteredReviews = mockReviews;

      if (options.status) {
        filteredReviews = filteredReviews.filter(r => r.status === options.status);
      }

      if (options.itemType) {
        filteredReviews = filteredReviews.filter(r => r.itemType === options.itemType);
      }

      // Apply pagination
      const limit = options.limit || 10;
      const offset = options.offset || 0;
      filteredReviews = filteredReviews.slice(offset, offset + limit);

      // Calculate stats
      const stats = {
        total: mockReviews.length,
        published: mockReviews.filter(r => r.status === 'published').length,
        pending: mockReviews.filter(r => r.status === 'pendingModeration').length,
        rejected: mockReviews.filter(r => r.status === 'rejected').length,
        averageRating: mockReviews
          .filter(r => r.status === 'published')
          .reduce((sum, r) => sum + r.rating, 0) / mockReviews.filter(r => r.status === 'published').length || 0
      };

      return {
        reviews: filteredReviews,
        stats
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Bulk moderate reviews (Admin only)
   */
  async bulkModerateReviews(
    adminId: string,
    moderationRequests: Array<{
      reviewId: string;
      action: 'approve' | 'reject';
      reason?: string;
    }>
  ): Promise<{
    moderated: Array<{
      reviewId: string;
      success: boolean;
      error?: string;
    }>;
    summary: {
      approved: number;
      rejected: number;
      failed: number;
    };
  }> {
    try {
      // Verify admin permissions
      const isAdmin = await this.userRepo.isUserAdmin(adminId);
      if (!isAdmin) {
        throw new Error('Unauthorized: Only administrators can bulk moderate reviews');
      }

      const moderated: Array<{
        reviewId: string;
        success: boolean;
        error?: string;
      }> = [];

      let approved = 0;
      let rejected = 0;
      let failed = 0;

      for (const request of moderationRequests) {
        try {
          await this.moderateReview(request.reviewId, adminId, request.action, request.reason);

          moderated.push({
            reviewId: request.reviewId,
            success: true
          });

          if (request.action === 'approve') {
            approved++;
          } else {
            rejected++;
          }
        } catch (error) {
          moderated.push({
            reviewId: request.reviewId,
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error'
          });
          failed++;
        }
      }

      return {
        moderated,
        summary: {
          approved,
          rejected,
          failed
        }
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  // Helper methods

  private async verifyPurchaseOrCompletion(
    userId: string,
    itemType: string,
    itemId: string
  ): Promise<boolean> {
    // Mock verification - in reality would check purchase/completion records
    return Math.random() > 0.2; // 80% have access
  }

  private async getExistingReview(
    userId: string,
    itemType: string,
    itemId: string
  ): Promise<any | null> {
    // Mock check for existing review
    return Math.random() > 0.8 ? null : { id: 'existingReview' }; // 20% already reviewed
  }

  private async applyModerationRules(review: any): Promise<{
    status: string;
    reason?: string;
  }> {
    // Simple moderation rules
    const comment = review.comment.toLowerCase();

    // Check for inappropriate content
    const inappropriateWords = ['spam', 'fake', 'scam', 'inappropriate'];
    const hasInappropriateContent = inappropriateWords.some(word =>
      comment.includes(word)
    );

    if (hasInappropriateContent) {
      return {
        status: 'rejected',
        reason: 'Contains inappropriate content'
      };
    }

    // Check comment length
    if (review.comment.length < 10) {
      return {
        status: 'rejected',
        reason: 'Review too short'
      };
    }

    // All reviews go through moderation for quality control
    return {
      status: 'pendingModeration',
      reason: 'Quality review pending'
    };
  }

  private generateMockReviews(itemType: string, itemId: string, count: number): any[] {
    const reviews = [];
    for (let i = 0; i < count; i++) {
      reviews.push({
        id: `review_${i}`,
        userId: `user_${Math.floor(Math.random() * 100)}`,
        itemType: itemType,
        itemId: itemId,
        rating: Math.floor(Math.random() * 5) + 1,
        title: `Review ${i + 1}`,
        comment: `This is a ${['great', 'good', 'average', 'poor', 'excellent'][Math.floor(Math.random() * 5)]} ${itemType}. ${['Highly recommended!', 'Could be better.', 'Worth the price.', 'Not what I expected.', 'Amazing experience!'][Math.floor(Math.random() * 5)]}`,
        status: ['published', 'published', 'published', 'pendingModeration', 'rejected'][Math.floor(Math.random() * 5)],
        createdAt: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000),
        helpfulVotes: Math.floor(Math.random() * 20),
        totalVotes: Math.floor(Math.random() * 25)
      });
    }
    return reviews;
  }

  private generateMockUserReviews(userId: string, count: number): any[] {
    const reviews = [];
    const itemTypes = ['course', 'resource', 'connect'];

    for (let i = 0; i < count; i++) {
      reviews.push({
        id: `review_${i}`,
        userId: userId,
        itemType: itemTypes[Math.floor(Math.random() * itemTypes.length)],
        itemId: `item_${Math.floor(Math.random() * 50)}`,
        rating: Math.floor(Math.random() * 5) + 1,
        title: `My Review ${i + 1}`,
        comment: `I ${['loved', 'enjoyed', 'found useful', 'learned a lot from', 'recommend'][Math.floor(Math.random() * 5)]} this item.`,
        status: ['published', 'published', 'pendingModeration', 'rejected'][Math.floor(Math.random() * 4)],
        createdAt: new Date(Date.now() - Math.random() * 60 * 24 * 60 * 60 * 1000)
      });
    }
    return reviews;
  }

  private async getUserVote(reviewId: string, userId: string): Promise<any | null> {
    // Mock check for existing vote
    return Math.random() > 0.7 ? null : { id: 'existingVote' }; // 30% already voted
  }

  private async applyAutoModeration(review: any, report: any): Promise<boolean> {
    // Auto-moderate based on report reason
    const seriousReasons = ['harassment', 'spam', 'inappropriateContent'];

    if (seriousReasons.includes(report.reason)) {
      return true; // Auto-flag for moderation
    }

    return false;
  }

  private async sendModerationNotification(
    userId: string,
    action: string,
    reason?: string
  ): Promise<boolean> {
    // Mock notification sending
    this.logInfo(`Moderation notification sent to user ${userId}: ${action}${reason ? ` - ${reason}` : ''}`);
    return true;
  }
}
